// src/pages/ServerDetail.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Card from '../components/Card';
import PageHeader from '../components/PageHeader';
import PrimaryButton from '../components/PrimaryButton';
import StatusBadge from '../components/StatusBadge';
import ErrorAlert from '../components/ErrorAlert';
import Skeleton, { SkeletonPageTitle } from '../components/Skeleton';
import UptimeHistory from '../components/UptimeHistory';
import { IconChevronRight, IconRefresh } from '../icons';
import { formatDate } from '../utils/format';

const ServerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [server, setServer] = useState(null);
  const [error, setError] = useState(null);
  const [checkingNow, setCheckingNow] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  const fetchServerData = useCallback(async () => {
    try {
      const response = await axios.get(`/api/servers/${id}`);
      setServer(response.data);
    } catch (err) {
      console.error('Error fetching server:', err);
      setError('Failed to load server data');
    }
  }, [id]);

  useEffect(() => { fetchServerData(); }, [fetchServerData]);

  const fetchRows = useCallback(async (params) => {
    const response = await axios.get(`/api/servers/${id}/aggregated-history`, { params });
    return response.data?.data || [];
  }, [id]);

  const handleManualCheck = async () => {
    try {
      setCheckingNow(true);
      await axios.post(`/api/servers/${id}/check`);
      await fetchServerData();
      setReloadToken((t) => t + 1);
    } catch (err) {
      console.error('Error performing manual check:', err);
      setError('Failed to perform health check');
    } finally {
      setCheckingNow(false);
    }
  };

  // Пока сервер грузится, страница уже стоит в своей раскладке: шапка и
  // статус — скелетоны, а история грузится параллельно (ей нужен только id).
  const statusCard = (
    <Card className="p-4 flex flex-col w-full">
      <div className="flex items-center justify-between gap-2 mb-4">
        <h2 className="text-[15px] font-medium text-ink">Current Status</h2>
        {server
          ? <StatusBadge status={server.lastCheckStatus} />
          : <Skeleton width={80} height={24} radius="full" />}
      </div>
      <div className="flex-1 flex flex-col justify-center">
        <span className="text-[10px] uppercase tracking-[0.4px] text-ink-muted">Last checked</span>
        {server ? (
          <span className="text-sm font-medium text-ink mt-0.5">
            {formatDate(server.lastCheckedAt)}
          </span>
        ) : (
          <div className="h-5 mt-0.5 flex items-center">
            <Skeleton width={140} height={14} />
          </div>
        )}
      </div>
    </Card>
  );

  const checkButton = (
    <PrimaryButton onClick={handleManualCheck} loading={checkingNow}>
      {!checkingNow && <IconRefresh size={14} />}
      {checkingNow ? 'Checking…' : 'Check now'}
    </PrimaryButton>
  );

  return (
    <div className="bg-page">
      <div className="w-full px-4 sm:px-8 flex flex-col gap-5">
        <button
          onClick={() => navigate('/')}
          className="inline-flex items-center gap-1 text-sm text-ink-muted hover:text-ink transition-colors w-fit"
        >
          <IconChevronRight size={16} className="rotate-180" />
          Back to Servers
        </button>

        {server ? (
          <PageHeader
            title={server.name}
            subtitle={[server.description, server.ipAddress && `IP: ${server.ipAddress}`]
              .filter(Boolean)
              .join(' · ')}
            actions={checkButton}
          />
        ) : (
          <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <SkeletonPageTitle />
            <div className="flex items-center gap-2 shrink-0">{checkButton}</div>
          </header>
        )}

        <ErrorAlert message={error} onDismiss={() => setError(null)} />

        <UptimeHistory
          title="Uptime History"
          metricLabel="Uptime"
          okKey="healthyCount"
          badKey="unhealthyCount"
          okLabel="Healthy"
          badLabel="Unhealthy"
          fetchRows={fetchRows}
          reloadToken={reloadToken}
          sideCard={statusCard}
          onError={setError}
        />
      </div>
    </div>
  );
};

export default ServerDetail;
